import { ApprenticeshipFormType } from '../../utils/sharedTypes'
import { Formtitle, Layout } from '../../components'
import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { MdNavigateNext } from 'react-icons/md'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { DateField } from '@mui/x-date-pickers'
import { emailValidator, phoneNumberValidator } from '../../utils/validators'
import { client } from '../../utils/client'
import Bugsnag from '@bugsnag/js'
import { AddressInput, AddressOption } from '../../components/AddressInput'
import React, { useEffect } from 'react'
type InformationPropsType = {
  form: ApprenticeshipFormType
  setForm: React.Dispatch<React.SetStateAction<ApprenticeshipFormType>>
}

const Information = ({ form, setForm }: InformationPropsType) => {
  const [address, setAddress] = React.useState<AddressOption | null>(null)
  const navigate = useNavigate()

  const validateAllFields = () => {
    const newErrors: Record<string, string> = {}

    // Validation des champs de base
    if (!form.data.first_name || form.data.first_name.length === 0) {
      newErrors['first_name'] = 'Veuillez renseigner un prénom'
    }

    if (!form.data.last_name || form.data.last_name.length === 0) {
      newErrors['last_name'] = 'Veuillez renseigner un nom de famille'
    }

    if (!form.data.nir || form.data.nir.length !== 15) {
      newErrors['nir'] = 'Veuillez renseigner un numéro de sécurité sociale valide'
    }

    if (!form.data.mobile_phone || !phoneNumberValidator(form.data.mobile_phone)) {
      newErrors['mobile_phone'] = 'Veuillez renseigner un numéro de téléphone valide'
    }

    if (!form.data.email || !emailValidator(form.data.email)) {
      newErrors['email'] = 'Veuillez renseigner un e-mail valide'
    }

    if (!form.data.gender || form.data.gender === '') {
      newErrors['gender'] = 'Veuillez renseigner votre sexe'
    }

    if (!form.data.nationality || form.data.nationality === '') {
      newErrors['nationality'] = 'Veuillez renseigner votre nationalité'
    }

    if (!form.data.birth_date || form.data.birth_date === '') {
      newErrors['birth_date'] = 'Veuillez renseigner votre date de naissance'
    } else if (!dayjs(form.data.birth_date).isValid()) {
      newErrors['birth_date'] = 'Veuillez entrer une date de naissance valide'
    }

    if (!form.data.street || form.data.street.length === 0) {
      newErrors['street'] = 'Veuillez renseigner une adresse'
    }

    if (!form.data.postal_code || form.data.postal_code.length === 0) {
      newErrors['postal_code'] = 'Veuillez renseigner un code postal'
    }

    if (!form.data.city || form.data.city.length === 0) {
      newErrors['city'] = 'Veuillez renseigner une ville'
    }

    if (!form.data.country || form.data.country.length === 0) {
      newErrors['country'] = 'Veuillez renseigner un pays'
    }

    if (!form.data.birth_city || form.data.birth_city.length === 0) {
      newErrors['birth_city'] = 'Veuillez renseigner une commune'
    }

    // Validation conditionnelle pour is_born_abroad
    if (form.data.is_born_abroad === false) {
      if (!form.data.birth_department || form.data.birth_department.length === 0) {
        newErrors['birth_department'] = 'Veuillez renseigner un département'
      } else if (!/^[0-9]{1,3}(A|B)?$/.test(form.data.birth_department)) {
        newErrors['birth_department'] = 'Veuillez renseigner un département valide'
      }
    } else if (form.data.is_born_abroad === true) {
      if (!form.data.birth_country || form.data.birth_country.length === 0) {
        newErrors['birth_country'] = 'Veuillez renseigner un pays'
      }
    }

    // Validation conditionnelle pour want_to_move_out
    if (form.data.want_to_move_out === true) {
      if (!form.data.move_out_city || form.data.move_out_city.length === 0) {
        newErrors['move_out_city'] = 'Veuillez renseigner une ville'
      }

      if (!form.data.move_out_postal_code || form.data.move_out_postal_code.length === 0) {
        newErrors['move_out_postal_code'] = 'Veuillez renseigner un code postal'
      }
    }

    // Validation conditionnelle pour has_legal_representative
    if (form.data.has_legal_representative === true) {
      if (!form.data.legal_representative_first_name || form.data.legal_representative_first_name.length === 0) {
        newErrors['legal_representative_first_name'] = 'Veuillez renseigner un prénom'
      }

      if (!form.data.legal_representative_last_name || form.data.legal_representative_last_name.length === 0) {
        newErrors['legal_representative_last_name'] = 'Veuillez renseigner un nom de famille'
      }

      if (!form.data.legal_representative_email || !emailValidator(form.data.legal_representative_email)) {
        newErrors['legal_representative_email'] = 'Veuillez renseigner un e-mail valide'
      }

      if (
        !form.data.legal_representative_mobile_phone ||
        !phoneNumberValidator(form.data.legal_representative_mobile_phone)
      ) {
        newErrors['legal_representative_mobile_phone'] = 'Veuillez renseigner un numéro de téléphone valide'
      }

      if (!form.data.legal_representative_street || form.data.legal_representative_street.length === 0) {
        newErrors['legal_representative_street'] = 'Veuillez renseigner une adresse'
      }

      if (!form.data.legal_representative_postal_code || form.data.legal_representative_postal_code.length === 0) {
        newErrors['legal_representative_postal_code'] = 'Veuillez renseigner un code postal'
      }

      if (!form.data.legal_representative_city || form.data.legal_representative_city.length === 0) {
        newErrors['legal_representative_city'] = 'Veuillez renseigner une ville'
      }

      if (!form.data.legal_representative_country || form.data.legal_representative_country.length === 0) {
        newErrors['legal_representative_country'] = 'Veuillez renseigner un pays'
      }
    }

    // Mettre à jour les erreurs dans le formulaire
    setForm((prevState) => ({
      ...prevState,
      errors: { ...prevState.errors, ...newErrors },
    }))

    // Retourner les erreurs pour pouvoir les utiliser immédiatement
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors }
  }

  const submitInfo = () => {
    // Valider tous les champs avant de soumettre
    const validation = validateAllFields()
    console.log(validation)
    if (!validation.isValid) {
      // Faire défiler vers le premier champ en erreur
      const errorKeys = [
        'first_name',
        'last_name',
        'nir',
        'mobile_phone',
        'email',
        'street',
        'postal_code',
        'city',
        'country',
        'gender',
        'nationality',
        'birth_date',
        'birth_city',
        'birth_department',
        'birth_country',
        'move_out_city',
        'move_out_postal_code',
        'legal_representative_first_name',
        'legal_representative_last_name',
        'legal_representative_email',
        'legal_representative_mobile_phone',
        'legal_representative_street',
        'legal_representative_postal_code',
        'legal_representative_city',
        'legal_representative_country',
      ]

      // Trouver le premier champ en erreur et faire défiler vers lui
      for (const key of errorKeys) {
        if (validation.errors[key]) {
          let field: HTMLElement | null = null

          // Gérer les cas spéciaux pour les Select
          if (key === 'gender') {
            field = document.getElementById('sex-label')?.closest('.MuiFormControl-root') as HTMLElement
          } else if (key === 'nationality') {
            field = document.getElementById('nationality-label')?.closest('.MuiFormControl-root') as HTMLElement
          } else {
            // Pour les autres champs, chercher par id
            field = document.getElementById(key) || (document.querySelector(`[id*="${key}"]`) as HTMLElement)
          }

          if (field) {
            setTimeout(() => {
              field?.scrollIntoView({ behavior: 'smooth', block: 'center' })
              // Essayer de focus sur le champ input si c'est un TextField
              const input = field?.querySelector('input') || field?.querySelector('textarea')
              if (input) {
                input.focus()
              }
            }, 100)
            break
          }
        }
      }
      return
    }

    client
      .patch(`/apprenticeship/public/${form.data.global_id}/`, form.data)
      .then((resp) => {
        if (resp.status === 200) {
          if (form.data.has_apprenticeship_company) {
            navigate({ pathname: './../entreprise/', search: location.search })
          } else {
            navigate({ pathname: './../profile/', search: location.search })
          }
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  useEffect(() => {
    setForm((prevState) => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          city: address?.city ?? '',
          postal_code: address?.postcode ?? '',
          street:
            address?.housenumber && address?.street
              ? `${address?.housenumber} ${address?.street}`
              : address?.street ?? '',
          country: address?.country ?? '',
          latitude: address?.latitude ?? null,
          longitude: address?.longitude ?? null,
        },
      }
    })
  }, [address])

  useEffect(() => {
    if (
      form.data.city &&
      form.data.street &&
      form.data.postal_code &&
      form.data.country &&
      form.data.latitude &&
      form.data.longitude
    ) {
      setAddress({
        label: `${form.data.street} ${form.data.postal_code} ${form.data.city}`,
        housenumber: form.data.street?.split(' ')[0],
        street: form.data.street?.split(' ').slice(1).join(' '),
        postcode: form.data.postal_code,
        city: form.data.city,
        country: form.data.country,
        latitude: form.data.latitude ?? undefined,
        longitude: form.data.longitude ?? undefined,
      })
    }
  }, [
    form.data.street,
    form.data.postal_code,
    form.data.city,
    form.data.country,
    form.data.latitude,
    form.data.longitude,
  ])

  const handleChange = (key: string, value: string | boolean | Record<string, string>) => {
    setForm((prevState) => {
      const errors = prevState.errors
      delete errors[key]

      switch (key) {
        case 'first_name':
          if ((value as string).length === 0) {
            errors['first_name'] = 'Veuillez renseigner un prénom'
          }
          break

        case 'last_name':
          if ((value as string).length === 0) {
            errors['last_name'] = 'Veuillez renseigner un nom de famille'
          }
          break

        case 'nir':
          if ((value as string).length !== 15) {
            errors['nir'] = 'Veuillez renseigner un numéro de sécurité sociale valide'
          }
          break

        case 'mobile_phone':
          if (!phoneNumberValidator(value as string)) {
            errors['mobile_phone'] = 'Veuillez renseigner un numéro de téléphone valide'
          }
          break

        case 'email':
          if (!emailValidator(value as string)) {
            errors['email'] = 'Veuillez renseigner un e-mail valide'
          }
          break

        case 'gender':
          if (value === '') {
            errors['gender'] = 'Veuillez renseigner votre sexe'
          }
          break

        case 'nationality':
          if (value === '') {
            errors['nationality'] = 'Veuillez renseigner votre nationalité'
          }
          break

        case 'birth_date':
          if (value === '') {
            errors['birth_date'] = 'Veuillez renseigner votre date de naissance'
          }
          break

        case 'postal_code':
          if ((value as string).length === 0) {
            errors['postal_code'] = 'Veuillez renseigner un code postal'
          }
          break

        case 'city':
          if ((value as string).length === 0) {
            errors['city'] = 'Veuillez renseigner une ville'
          }
          break

        case 'legal_representative_country':
        case 'birth_country':
        case 'country':
          if ((value as string).length === 0) {
            errors[key] = 'Veuillez renseigner un pays'
          }
          break

        case 'street':
          if ((value as string).length === 0) {
            errors['street'] = 'Veuillez renseigner une adresse'
          }
          break

        case 'birth_department':
          if ((value as string).length === 0) {
            errors['birth_department'] = 'Veuillez renseigner un département'
          }

          if (!/^[0-9]{1,3}(A|B)?$/.test(value as string)) {
            errors['birth_department'] = 'Veuillez renseigner un département valide'
          }
          break

        case 'birth_city':
          if ((value as string).length === 0) {
            errors['birth_city'] = 'Veuillez renseigner une commune'
          }
          break

        case 'legal_representative_email':
          if (!emailValidator(value as string)) {
            errors['legal_representative_email'] = 'Veuillez renseigner un e-mail valide'
          }
          break

        case 'legal_representative_first_name':
          if ((value as string).length === 0) {
            errors['legal_representative_first_name'] = 'Veuillez renseigner un prénom'
          }
          break

        case 'legal_representative_last_name':
          if ((value as string).length === 0) {
            errors['legal_representative_last_name'] = 'Veuillez renseigner un nom de famille'
          }
          break

        case 'legal_representative_mobile_phone':
          if (!phoneNumberValidator(value as string)) {
            errors['legal_representative_mobile_phone'] = 'Veuillez renseigner un numéro de téléphone valide'
          }
          break

        case 'legal_representative_street':
          if ((value as string).length === 0) {
            errors['legal_representative_street'] = 'Veuillez renseigner une adresse'
          }
          break

        case 'legal_representative_postal_code':
          if ((value as string).length === 0) {
            errors['legal_representative_postal_code'] = 'Veuillez renseigner un code postal'
          }
          break

        case 'legal_representative_city':
          if ((value as string).length === 0) {
            errors['legal_representative_city'] = 'Veuillez renseigner une ville'
          }
          break

        case 'move_out_city':
          if ((value as string).length === 0) {
            errors['move_out_city'] = 'Veuillez renseigner une ville'
          }
          break

        case 'move_out_postal_code':
          if ((value as string).length === 0) {
            errors['move_out_postal_code'] = 'Veuillez renseigner un code postal'
          }
          break
      }

      return {
        ...prevState,
        data: {
          ...prevState.data,
          [key]: value,
        },
        errors: errors,
      }
    })
  }

  return (
    <Layout form={form.data} steps={form.steps}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submitInfo()
        }}
      >
        <Formtitle
          title="Informations personnelles"
          subtitle="Remplissez le formulaire ci-dessous pour poursuivre votre inscription"
        />
        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Prénom"
              variant="outlined"
              id="first_name"
              required
              value={form.data.first_name}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('first_name', e?.target.value)
              }}
              error={Boolean(form.errors.first_name)}
              helperText={form.errors.first_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nom"
              id="last_name"
              variant="outlined"
              required
              value={form.data.last_name}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('last_name', e?.target.value)
              }}
              error={Boolean(form.errors.last_name)}
              helperText={form.errors.last_name}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="N° de sécurité sociale (15 chiffres)"
              id="nir"
              variant="outlined"
              required
              value={form.data.nir}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('nir', e?.target.value)
              }}
              error={Boolean(form.errors.nir)}
              helperText={form.errors.nir}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="N° de mobile"
              id="mobile_phone"
              variant="outlined"
              required
              value={form.data.mobile_phone}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('mobile_phone', e?.target.value)
              }}
              error={Boolean(form.errors.mobile_phone)}
              helperText={form.errors.mobile_phone}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Adresse e-mail"
              id="email"
              variant="outlined"
              required
              value={form.data.email}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('email', e?.target.value)
              }}
              error={Boolean(form.errors.email)}
              helperText={form.errors.email}
            />
          </Grid>

          <Grid item xs={12}>
            <AddressInput
              address={address}
              onAddressChange={(value) => {
                setAddress(value)
              }}
              error={form.errors}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Checkbox
                id="want_to_move_out"
                sx={{ ml: -1, mt: { xs: 0, sm: 0, md: 0 } }}
                checked={form.data.want_to_move_out ?? false}
                onClick={() => {
                  handleChange('want_to_move_out', !form.data.want_to_move_out)
                }}
              />

              <Typography variant="body1" fontWeight="">
                Je prévois de déménager
              </Typography>
            </Grid>
          </Grid>

          {form.data.want_to_move_out && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ville de déménagement"
                  id="move_out_city"
                  variant="outlined"
                  required
                  value={form.data.move_out_city}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('move_out_city', e?.target.value)
                  }}
                  error={Boolean(form.errors.move_out_city)}
                  helperText={form.errors.move_out_city}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Code postal de déménagement"
                  id="move_out_postal_code"
                  variant="outlined"
                  required
                  value={form.data.move_out_postal_code}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('move_out_postal_code', e?.target.value)
                  }}
                  error={Boolean(form.errors.move_out_postal_code)}
                  helperText={form.errors.move_out_postal_code}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6} lg={4}>
            <FormControl fullWidth required>
              <InputLabel id="sex-label">Sexe</InputLabel>
              <Select
                fullWidth
                labelId="sex-label"
                label="Sexe"
                onChange={(e) => {
                  handleChange('gender', e.target.value)
                }}
                value={form.data.gender}
                error={Boolean(form.errors.gender)}
              >
                <MenuItem value="Homme">Homme</MenuItem>
                <MenuItem value="Femme">Femme</MenuItem>
              </Select>
              <FormHelperText>{form.errors.gender}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <FormControl fullWidth required>
              <InputLabel id="nationality-label">Nationalité</InputLabel>
              <Select
                labelId="nationality-label"
                label="Nationalité"
                onChange={(e) => {
                  handleChange('nationality', e.target.value)
                }}
                value={form.data.nationality}
              >
                <MenuItem value="Française">Française</MenuItem>
                <MenuItem value="Union Européenne">Union européenne</MenuItem>
                <MenuItem value="Extra Européen">Hors Union européenne</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <DateField
              fullWidth
              id="birth_date"
              label="Date de naissance"
              format="DD/MM/YYYY"
              disableFuture
              value={form.data.birth_date ? dayjs(form.data.birth_date) : null}
              onChange={(value) => {
                //HACKY
                if (value && dayjs(value).isValid() && `${dayjs(value).year()}`.length === 4) {
                  handleChange('birth_date', dayjs(value).format('YYYY-MM-DD'))
                }
              }}
              slots={{
                textField: (params) => (
                  <TextField
                    id="birthdate_textField"
                    {...params}
                    fullWidth
                    onBlur={() => {
                      if (!dayjs(form.data.birth_date).isValid()) {
                        setForm((prevState) => {
                          return {
                            ...prevState,
                            errors: {
                              ...prevState.errors,
                              birth_date: 'Veuillez entrer une date de naissance valide',
                            },
                          }
                        })
                      }
                    }}
                    error={Boolean(form.errors.birth_date)}
                    helperText={form.errors.birth_date}
                  />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid item container>
              <Typography variant="body1" fontWeight="bold">
                Je suis né à l'étranger
              </Typography>

              <Grid container alignItems="center">
                <Checkbox
                  id="is_born_abroad"
                  sx={{ ml: -1, mt: { xs: 0, sm: 0, md: 0 } }}
                  checked={form.data.is_born_abroad === true || false}
                  onClick={() => {
                    handleChange('is_born_abroad', true)
                  }}
                />

                <Typography>Oui</Typography>
              </Grid>
              <Grid container alignItems="center">
                <Checkbox
                  id="is_born_abroad"
                  sx={{ ml: -1, mt: { xs: 0, sm: 0, md: 0 } }}
                  checked={form.data.is_born_abroad === false || false}
                  onClick={() => {
                    handleChange('is_born_abroad', false)
                    handleChange('birth_department', '')
                    handleChange('birth_city', '')
                    handleChange('birth_country', '')
                  }}
                />

                <Typography>Non</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Commune"
              id="birth_city"
              variant="outlined"
              required
              value={form.data.birth_city}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                handleChange('birth_city', e?.target.value)
              }}
              error={Boolean(form.errors.birth_city)}
              helperText={form.errors.birth_city}
            />
          </Grid>

          {form.data.is_born_abroad === false ? (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="N° Département"
                id="department"
                variant="outlined"
                required
                value={form.data.birth_department}
                onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                  handleChange('birth_department', e?.target.value)
                }}
                error={Boolean(form.errors.birth_department)}
                helperText={form.errors.birth_department}
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pays de naissance"
                id="birth_country"
                variant="outlined"
                required
                value={form.data.birth_country}
                onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                  handleChange('birth_country', e?.target.value)
                }}
                error={Boolean(form.errors.birth_country)}
                helperText={form.errors.birth_country}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Grid item container>
              <Typography variant="body1" fontWeight="bold">
                J'ai déjà trouvé une entreprise
              </Typography>

              <Grid container alignItems="center">
                <Checkbox
                  id="has_apprenticeship_company"
                  sx={{ ml: -1, mt: { xs: 0, sm: 0, md: 0 } }}
                  checked={form.data.has_apprenticeship_company === true || false}
                  onClick={() => {
                    handleChange('has_apprenticeship_company', true)
                  }}
                />

                <Typography>Oui</Typography>
              </Grid>
              <Grid container alignItems="center">
                <Checkbox
                  id="has_apprenticeship_company"
                  sx={{ ml: -1, mt: { xs: 0, sm: 0, md: 0 } }}
                  checked={form.data.has_apprenticeship_company === false || false}
                  onClick={() => {
                    handleChange('has_apprenticeship_company', false)
                  }}
                />

                <Typography>Non</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid item container>
              <Typography variant="body1" fontWeight="bold">
                J'ai un représentant légal
              </Typography>

              <Grid container alignItems="center">
                <Checkbox
                  id="has_legal_representative"
                  sx={{ ml: -1, mt: { xs: 0, sm: 0, md: 0 } }}
                  checked={form.data.has_legal_representative === true || false}
                  onClick={() => {
                    handleChange('has_legal_representative', true)
                  }}
                />

                <Typography>Oui</Typography>
              </Grid>
              <Grid container alignItems="center">
                <Checkbox
                  id="has_legal_representative"
                  sx={{ ml: -1, mt: { xs: 0, sm: 0, md: 0 } }}
                  checked={form.data.has_legal_representative === false || false}
                  onClick={() => {
                    handleChange('has_legal_representative', false)
                    handleChange('legal_representative_email', '')
                    handleChange('legal_representative_first_name', '')
                    handleChange('legal_representative_last_name', '')
                  }}
                />

                <Typography>Non</Typography>
              </Grid>
            </Grid>
          </Grid>

          {form.data.has_legal_representative === true && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Prénom du représentant légal"
                  id="legal_representative_first_name"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_first_name}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_first_name', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_first_name)}
                  helperText={form.errors.legal_representative_first_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom du représentant légal"
                  id="legal_representativ_last_name"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_last_name}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_last_name', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_last_name)}
                  helperText={form.errors.legal_representative_last_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Adresse e-mail du représentant légal"
                  id="legal_representative_email"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_email}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_email', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_email)}
                  helperText={form.errors.legal_representative_email}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="N° de mobile du représentant légal"
                  id="legal_representative_mobile_phone"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_mobile_phone}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_mobile_phone', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_mobile_phone)}
                  helperText={form.errors.legal_representative_mobile_phone}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse du représentant légal"
                  id="legal_representative_street"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_street}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_street', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_street)}
                  helperText={form.errors.legal_representative_street}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Code postal du représentant légal"
                  id="legal_representative_postal_code"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_postal_code}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_postal_code', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_postal_code)}
                  helperText={form.errors.legal_representative_postal_code}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Ville du représentant légal"
                  id="legal_representative_city"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_city}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_city', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_city)}
                  helperText={form.errors.legal_representative_city}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Pays du représentant légal"
                  id="legal_representative_country"
                  variant="outlined"
                  required
                  value={form.data.legal_representative_country}
                  onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleChange('legal_representative_country', e?.target.value)
                  }}
                  error={Boolean(form.errors.legal_representative_country)}
                  helperText={form.errors.legal_representative_country}
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            sx={{
              height: {
                xs: 45,
              },
            }}
            endIcon={<MdNavigateNext />}
          >
            Suivant
          </Button>
        </Grid>
      </form>
    </Layout>
  )
}

export default Information
